/**
 * @file    FiltersSection.tsx
 * @scope   VIEW
 * @summary "Filters" accordion of the drawer main view: calendar range, hierarchy filters and
 *          advanced filters, plus the date-range modal it opens.
 * @consumes Accordion, DateRangeInput, HierarchyFilters, AdvancedFiltersComponent, DiyCalendarContainer
 * @produces JSX only
 * @sideEffects None — every change is propagated through the injected callbacks.
 * @notes LEGACY-ONLY: Enterprise widgets configure their filters inside the FieldsManager,
 *        so the caller must not render this section for them.
 *        WHY the accordion and modal flags live here: they are pure UI state nobody else reads,
 *        so keeping them in the drawer only forced the whole drawer to re-render on every toggle.
 */
import React, { useCallback, useState } from 'react';
import Accordion from 'core-ui/accordionDrawer/accordionDrawer';
import DateRangeInput from 'core-ui/filteringDatePicker/filteringDatePicker';
import HierarchyFilters from 'core-ui/cx2HierarchyFilter/cx2HierarchyFilter';
import AdvancedFiltersComponent from 'core-ui/cx2AdvancedFilters/cx2AdvancedFilters';
import ModalComponent from 'components/DiyCalendarContainer/DiyCalendarContainer';

export interface FiltersSectionProps {
  /** Widget id, required by the hierarchy/advanced filter pickers to scope their state. */
  widgetId: any;
  /** True when the calendar range picker applies to the current chart. */
  showCalendar: boolean;
  /** Filter catalogue offered by the date-range modal. */
  chartFilters: any;
  /** Commits the date range picked in the modal. */
  onSelectDateRange: (range: any) => void;
  /** Currently selected date range label. */
  dateSelection: any;
  /** Persisted filter selection used to seed the date picker. */
  currentSelection: any;
  showHierarchyFilters?: boolean;
  hierarchyFiltersValues: any;
  onHierarchyFiltersChange: (values: any) => void;
  showAdvancedFilters?: boolean;
  advancedFiltersValues: any;
  onAdvancedFiltersChange: (values: any) => void;
}

const FiltersSection: React.FC<FiltersSectionProps> = ({
  widgetId,
  showCalendar,
  chartFilters,
  onSelectDateRange,
  dateSelection,
  currentSelection,
  showHierarchyFilters,
  hierarchyFiltersValues,
  onHierarchyFiltersChange,
  showAdvancedFilters,
  advancedFiltersValues,
  onAdvancedFiltersChange,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [modalIsOpen, setModalIsOpen] = useState(false);

  const openModal = useCallback(() => setModalIsOpen(true), []);
  const closeModal = useCallback(() => setModalIsOpen(false), []);

  return (
    <div className="diy-drawer-section">
      <ModalComponent
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        onSelectDateRange={onSelectDateRange}
        chartFilters={chartFilters}
        currentSelection={currentSelection}
      />
      <Accordion title="Filters" isExpanded={isExpanded} onToggle={setIsExpanded}>
        {showCalendar ? (
          <div className="">
            <label className="diy-filters-label">Calendar</label>
            <DateRangeInput
              inputTitle=""
              handleOpenMoldal={openModal}
              dateSelection={dateSelection}
              currentSelection={currentSelection}
            />
          </div>
        ) : (
          <></>
        )}
        {showHierarchyFilters ? (
          <div className="diy-drawer-hierarchy-filters-section">
            <label className="diy-filters-label">Filters</label>
            <HierarchyFilters
              {...{
                onSetHierarchyFilters: onHierarchyFiltersChange,
                selectedHierarchyValues: hierarchyFiltersValues,
                id: widgetId,
              }}
            />
          </div>
        ) : (
          <></>
        )}
        {showAdvancedFilters ? (
          <div className="diy-drawer-advanced-filters-section">
            <label className="diy-filters-label">Advanced Filters</label>
            <AdvancedFiltersComponent
              {...{
                onSetAdvancedFilters: onAdvancedFiltersChange,
                selectedAdvancedFiltersValues: advancedFiltersValues,
                id: widgetId,
              }}
            />
          </div>
        ) : (
          <></>
        )}
      </Accordion>
    </div>
  );
};

export default React.memo(FiltersSection);
